import { onboardingManager } from "@core/service-container";
import { OnboardingProgramQuickstartStep } from "@domain/onboarding/onboarding";
import { useObservable } from "micro-observables";
import { complementaryGuide3ForceVisible, hideComplementaryGuide3 } from "@ui/pages/program-builder/quickstart/complementary-tooltip-signal";
import { alternativeGuide3ForceVisible, hideAlternativeGuide3, setAlternativeGuide3WasCompleted, clearAlternativePanelQuickstartGuide } from "@ui/pages/program-builder/quickstart/alternative-highlight-signal";
import { useCallback, useEffect, useRef, useState } from "react";
import { libraryFilterManager } from "@core/service-container";

export function useLibraryGuides(): {
    showComplementaryGuide3Panel: boolean;
    showAlternativeGuide3Panel: boolean;
    onCloseOnboardingPanel: () => Promise<void>;
} {
    const lastAddedExerciseMetadata = useObservable(libraryFilterManager.lastAddedExerciseMetadata);

    const forceComplementaryGuide3 = useObservable(complementaryGuide3ForceVisible);
    const forceAlternativeGuide3 = useObservable(alternativeGuide3ForceVisible);
    const showComplementaryGuide3Panel = forceComplementaryGuide3;
    const showAlternativeGuide3Panel = forceAlternativeGuide3;

    const [wasGuide3Available, setWasGuide3Available] = useState(false);

    useEffect(() => {
        if (showComplementaryGuide3Panel || showAlternativeGuide3Panel) {
            setWasGuide3Available(true);
        }
    }, [showComplementaryGuide3Panel, showAlternativeGuide3Panel]);

    useEffect(() => {
        if (forceComplementaryGuide3 && wasGuide3Available && !showComplementaryGuide3Panel) {
            hideComplementaryGuide3();
        }
    }, [forceComplementaryGuide3, wasGuide3Available, showComplementaryGuide3Panel]);

    useEffect(() => {
        if (forceAlternativeGuide3 && wasGuide3Available && !showAlternativeGuide3Panel) {
            hideAlternativeGuide3();
            setAlternativeGuide3WasCompleted();
        }
    }, [forceAlternativeGuide3, wasGuide3Available, showAlternativeGuide3Panel]);

    const lastAddedSnapshotRef = useRef<typeof lastAddedExerciseMetadata | null>(null);
    const lastAddedComplementarySnapshotRef = useRef<typeof lastAddedExerciseMetadata | null>(null);
    const prevAltVisibleRef = useRef<boolean>(false);
    const prevCompVisibleRef = useRef<boolean>(false);
    useEffect(() => {
        if (showAlternativeGuide3Panel && !prevAltVisibleRef.current) {
            lastAddedSnapshotRef.current = lastAddedExerciseMetadata ?? null;
        }
        if (!showAlternativeGuide3Panel && prevAltVisibleRef.current) {
            lastAddedSnapshotRef.current = null;
        }
        prevAltVisibleRef.current = showAlternativeGuide3Panel;
    }, [showAlternativeGuide3Panel, lastAddedExerciseMetadata]);

    useEffect(() => {
        if (showComplementaryGuide3Panel && !prevCompVisibleRef.current) {
            lastAddedComplementarySnapshotRef.current = lastAddedExerciseMetadata ?? null;
        }
        if (!showComplementaryGuide3Panel && prevCompVisibleRef.current) {
            lastAddedComplementarySnapshotRef.current = null;
        }
        prevCompVisibleRef.current = showComplementaryGuide3Panel;
    }, [showComplementaryGuide3Panel, lastAddedExerciseMetadata]);

    useEffect(() => {
        if (
            showComplementaryGuide3Panel &&
            wasGuide3Available &&
            lastAddedExerciseMetadata?.isComplementary &&
            lastAddedExerciseMetadata !== lastAddedComplementarySnapshotRef.current
        ) {
            hideComplementaryGuide3();
        }
        if (
            showAlternativeGuide3Panel &&
            wasGuide3Available &&
            lastAddedExerciseMetadata?.isAlternative &&
            lastAddedExerciseMetadata !== lastAddedSnapshotRef.current
        ) {
            hideAlternativeGuide3();
            setAlternativeGuide3WasCompleted();
        }
    }, [showComplementaryGuide3Panel, showAlternativeGuide3Panel, wasGuide3Available, lastAddedExerciseMetadata]);

    const onCloseOnboardingPanel = useCallback(async () => {
        if (showComplementaryGuide3Panel) {
            await onboardingManager.passStepOnboarding(OnboardingProgramQuickstartStep.COMPLEMENTARY_EXERCISES_GUIDE_03);
            hideComplementaryGuide3();
        }
        if (showAlternativeGuide3Panel) {
            await onboardingManager.passStepOnboarding(OnboardingProgramQuickstartStep.ALTERNATIVE_EXERCISES_GUIDE_02);
            clearAlternativePanelQuickstartGuide();
            hideAlternativeGuide3();
            setAlternativeGuide3WasCompleted();
        }
    }, [showComplementaryGuide3Panel, showAlternativeGuide3Panel]);

    return {
        showComplementaryGuide3Panel,
        showAlternativeGuide3Panel,
        onCloseOnboardingPanel,
    };
}
